import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { apiEndpoints, handleApiError } from '../services/api';
import { createApiUrlWithPrefix, createApiUrl, getUserImageUrl, getHorseImageUrl } from '../config/api';
import Modal, { ModalFooterBar } from './ui/Modal';
import {
  XCircle,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Droplets,
  FileText,
  Camera,
  Shield,
  Clock,
  CreditCard,
  Award,
  Gift,
  Zap
} from 'lucide-react';

function UserDetailsModal({ isOpen, onClose, user }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [horses, setHorses] = useState([]);
  const [loadingHorses, setLoadingHorses] = useState(false);
  const [forfait, setForfait] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [loadingForfait, setLoadingForfait] = useState(false);
  const [loadingInsurance, setLoadingInsurance] = useState(false);

  // Fetch horses for owners
  useEffect(() => {
    const fetchHorses = async () => {
      if (user?.role === 1 && user?.id) {
        try {
          setLoadingHorses(true);
          const response = await axios.get(createApiUrlWithPrefix(`horses/owner/${user.id}`), {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          setHorses(response.data);
        } catch (error) {
          console.error('Error fetching horses:', error);
          setHorses([]);
        } finally {
          setLoadingHorses(false);
        }
      }
    };

    if (isOpen) {
      fetchHorses();
    }
  }, [isOpen, user?.id, user?.role]);

  // Fetch forfait and insurance for riders
  useEffect(() => {
    const fetchRiderData = async () => {
      if (user?.role === 0 && user?.id) { // 0 = Rider
        try {
          // Fetch forfait
          setLoadingForfait(true);
          try {
            const forfaitResponse = await axios.get(createApiUrlWithPrefix(`RiderPayment/rider/${user.id}/forfait`), {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            setForfait(forfaitResponse.data);
          } catch (error) {
            if (error.response?.status !== 404) {
              console.error('Error fetching forfait:', error);
            }
            setForfait(null);
          } finally {
            setLoadingForfait(false);
          }

          // Fetch insurance
          setLoadingInsurance(true);
          try {
            const insuranceResponse = await axios.get(createApiUrlWithPrefix(`RiderPayment/rider/${user.id}/insurance`), {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            setInsurance(insuranceResponse.data);
          } catch (error) {
            if (error.response?.status !== 404) {
              console.error('Error fetching insurance:', error);
            }
            setInsurance(null);
          } finally {
            setLoadingInsurance(false);
          }
        } catch (error) {
          console.error('Error fetching rider data:', error);
        }
      }
    };

    if (isOpen) {
      fetchRiderData();
    }
  }, [isOpen, user?.id, user?.role]);

  if (!isOpen || !user) return null;

  const getRoleName = (role) => {
    const roleMap = {
      0: 'Rider',
      1: 'Owner', 
      2: 'Coach',
      3: 'Admin',
      4: 'SuperAdmin'
    };
    return roleMap[role] || 'Unknown';
  };

  const getRiderLevelName = (level) => {
    const levelMap = {
      0: 'Beginner',
      1: 'Intermediate',
      2: 'Professional'
    };
    return levelMap[level] || 'Not specified';
  };

  const getRoleBadgeColor = (role) => {
    const colorMap = {
      0: 'bg-yellow-100 text-yellow-800',
      1: 'bg-green-100 text-green-800',
      2: 'bg-blue-100 text-blue-800',
      3: 'bg-purple-100 text-purple-800',
      4: 'bg-red-100 text-red-800'
    };
    return colorMap[role] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status) => {
    return status === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0001-01-01T00:00:00') return 'Not specified';
    return new Date(dateString).toLocaleDateString();
  };

  const calculateAge = (birthDate) => {
    if (!birthDate || birthDate === '0001-01-01T00:00:00') return 'Not specified';
    return new Date().getFullYear() - new Date(birthDate).getFullYear();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="primary"
      size="2xl"
      dir={isRTL ? 'rtl' : 'ltr'}
      headerContent={
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-4 min-w-0">
            <div className="relative shrink-0">
              {getUserImageUrl(user) ? (
                <img
                  src={getUserImageUrl(user)}
                  alt={user.name || 'User'}
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-4 border-white/30 shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/30 ${getUserImageUrl(user) ? 'hidden' : 'flex'}`}>
                <span className="text-xl font-bold">
                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold truncate">{user.name || 'Unknown User'}</h2>
              <p className="text-sm opacity-90 truncate">{user.email}</p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                  {getRoleName(user.role)}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                  {user.status === 0 ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 transition-colors shrink-0"
            aria-label="Close"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>
      }
      footer={
        <ModalFooterBar>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Close
          </button>
        </ModalFooterBar>
      }
    >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Personal Information */}
            <div className="space-y-6 p-4 sm:p-6 lg:p-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-primary-600" />
                  Personal Information
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">{user.email || 'Not specified'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <User className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Username</p>
                      <p className="font-mono font-medium">{user.username || 'Not specified'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Phone Number</p>
                      <p className="font-medium">{user.phoneNumber || 'Not specified'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Birth Date</p>
                      <p className="font-medium">{formatDate(user.birthDate)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Age</p>
                      <p className="font-medium">{calculateAge(user.birthDate)} years old</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="font-medium">{user.address || 'Not specified'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Droplets className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Blood Type</p>
                      <p className="font-medium">{user.bloodType || 'Not specified'}</p>
                    </div>
                  </div>

                  {/* Rider Level - Only show for Riders */}
                  {user.role === 0 && (
                    <div className="flex items-center">
                      <Award className="w-4 h-4 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Rider Level</p>
                        <p className="font-medium">{getRiderLevelName(user.level)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Account Information */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-primary-600" />
                  Account Information
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Created At</p>
                      <p className="font-medium">{new Date(user.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Last Updated</p>
                      <p className="font-medium">{new Date(user.updatedAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  {user.lastLoginAt && (
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm text-gray-500">Last Login</p>
                        <p className="font-medium">{new Date(user.lastLoginAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center">
                    <Award className="w-4 h-4 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Points</p>
                      <p className="font-medium">{user.points || 0} points</p>
                    </div>
                  </div>
                  
                  {user.mustChangePassword && (
                    <div className="flex items-center">
                      <Shield className="w-4 h-4 text-orange-400 mr-3" />
                      <div>
                        <p className="text-sm text-orange-600">Password Status</p>
                        <p className="font-medium text-orange-600">Must change password</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Files and Documents */}
            <div className="space-y-6 p-4 sm:p-6 lg:p-8">
              {/* Profile Photo */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Camera className="w-5 h-5 mr-2 text-primary-600" />
                  Profile Photo
                </h3>
                
                {getUserImageUrl(user) ? (
                  <div className="text-center">
                    <img 
                      src={getUserImageUrl(user)} 
                      alt={user.name || 'User'} 
                      className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 shadow-lg mx-auto mb-4"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden">
                      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border-4 border-gray-200 shadow-lg mx-auto mb-4">
                        <span className="text-4xl font-bold text-gray-400">
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Current Photo</p>
                    <a 
                      href={getUserImageUrl(user)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800 text-sm underline"
                    >
                      View Full Size
                    </a>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border-4 border-gray-200 shadow-lg mx-auto mb-4">
                      <span className="text-4xl font-bold text-gray-400">
                        {user.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">No photo uploaded</p>
                  </div>
                )}
              </div>

              {/* Medical Certificate */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-primary-600" />
                  Medical Certificate
                </h3>
                
                {user.pjDocumentPath ? (
                  <div className="text-center">
                    <div className="w-32 h-32 bg-gray-100 rounded-xl border-4 border-gray-200 shadow-lg mx-auto mb-4 flex items-center justify-center">
                      <FileText className="w-16 h-16 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Current Medical Certificate</p>
                    <p className="text-xs text-gray-500 mb-2">{user.pjDocumentPath.split('/').pop()}</p>
                    <a 
                      href={createApiUrl(user.pjDocumentPath)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800 text-sm underline"
                    >
                      View Medical Certificate
                    </a>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-32 h-32 bg-gray-100 rounded-xl border-4 border-gray-200 shadow-lg mx-auto mb-4 flex items-center justify-center">
                      <FileText className="w-16 h-16 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600">No medical certificate uploaded</p>
                  </div>
                )}
              </div>

              {/* Birth Certificate */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-primary-600" />
                  Birth Certificate
                </h3>
                
                {user.birthCertificatePath ? (
                  <div className="text-center">
                    <div className="w-32 h-32 bg-green-50 rounded-xl border-4 border-green-200 shadow-lg mx-auto mb-4 flex items-center justify-center">
                      <FileText className="w-16 h-16 text-green-400" />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Current Document</p>
                    <a 
                      href={createApiUrl(user.birthCertificatePath)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800 text-sm underline"
                    >
                      View Document
                    </a>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-32 h-32 bg-gray-100 rounded-xl border-4 border-gray-200 shadow-lg mx-auto mb-4 flex items-center justify-center">
                      <FileText className="w-16 h-16 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-600">No birth certificate uploaded</p>
                  </div>
                )}
              </div>

              {/* Additional Info for Owners */}
              {user.role === 1 && (
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Zap className="w-5 h-5 mr-2 text-primary-600" />
                    Horses Owned
                  </h3>
                  
                  {loadingHorses ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <p className="text-sm text-gray-600 mt-2">Loading horses...</p>
                    </div>
                  ) : horses.length === 0 ? (
                    <div className="text-center py-4">
                      <Zap className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">No horses found</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {horses.map((horse) => (
                        <div key={horse.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center space-x-3">
                            {horse.imagePath ? (
                              <img 
                                src={getHorseImageUrl(horse.imagePath)} 
                                alt={horse.name} 
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className={`w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center ${horse.imagePath ? 'hidden' : 'flex'}`}>
                              <Zap className="w-5 h-5 text-gray-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{horse.name}</p>
                              <p className="text-sm text-gray-600">
                                {horse.box && `Box: ${horse.box}`}
                                {horse.box && horse.age && ' • '}
                                {horse.age && `Age: ${horse.age} years`}
                              </p>
                              <p className={`text-xs font-medium ${horse.ownerRides ? 'text-green-600' : 'text-blue-600'}`}>
                                {horse.ownerRides ? 'Ridden by Owner' : 'Ridden by Riders'}
                              </p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            horse.status === 0 ? 'bg-green-100 text-green-800' :
                            horse.status === 1 ? 'bg-yellow-100 text-yellow-800' :
                            horse.status === 2 ? 'bg-gray-100 text-gray-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {horse.status === 0 ? 'Active' :
                             horse.status === 1 ? 'Inactive' :
                             horse.status === 2 ? 'Retired' : 'Sold'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Additional Info for Riders */}
              {user.role === 0 && (
                <>
                  {/* Insurance Status */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Shield className="w-5 h-5 mr-2 text-primary-600" />
                      Insurance Status
                    </h3>
                    
                    {loadingInsurance ? (
                      <div className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        <p className="text-sm text-gray-600 mt-2">Loading insurance...</p>
                      </div>
                    ) : insurance ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-600">Status</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${insurance.isValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {insurance.isValid ? 'Active' : 'Expired'}
                            </span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount:</span>
                              <span className="font-medium">{insurance.amount.toLocaleString()} DA</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Start Date:</span>
                              <span className="font-medium">{new Date(insurance.startDate).toLocaleDateString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Expiry Date:</span>
                              <span className="font-medium">{new Date(insurance.expiryDate).toLocaleDateString()}</span>
                            </div>
                            {insurance.isValid && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Days Remaining:</span>
                                <span className="font-medium text-green-600">
                                  {Math.ceil((new Date(insurance.expiryDate) - new Date()) / (1000 * 60 * 60 * 24))} days
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <Shield className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">No insurance found</p>
                        <p className="text-xs text-gray-500 mt-1">Rider needs to purchase annual insurance</p>
                      </div>
                    )}
                  </div>

                  {/* Forfait Status */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CreditCard className="w-5 h-5 mr-2 text-primary-600" />
                      Monthly Forfait
                    </h3>
                    
                    {loadingForfait ? (
                      <div className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                        <p className="text-sm text-gray-600 mt-2">Loading forfait...</p>
                      </div>
                    ) : forfait ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-600">Status</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${forfait.isValid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {forfait.isValid ? 'Active' : 'Expired'}
                            </span>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount:</span>
                              <span className="font-medium">{forfait.amount.toLocaleString()} DA</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Month:</span>
                              <span className="font-medium">{new Date(forfait.year, forfait.month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Expiry Date:</span>
                              <span className="font-medium">{new Date(forfait.expiryDate).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Lessons Information */}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className="text-sm font-semibold text-blue-900 mb-3">Lessons Information</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-blue-700">Total Lessons:</span>
                              <span className="font-medium text-blue-900">{forfait.totalLessons}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-blue-700">Used Lessons:</span>
                              <span className="font-medium text-blue-900">{forfait.usedLessons}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-blue-700">Remaining Lessons:</span>
                              <span className="font-medium text-green-600">{forfait.remainingLessons}</span>
                            </div>
                            {forfait.extraLessonCount > 0 && (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-blue-700">Extra Lessons:</span>
                                  <span className="font-medium text-blue-900">{forfait.extraLessonCount}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-blue-700">Extra Lessons Amount:</span>
                                  <span className="font-medium text-blue-900">{forfait.extraLessonsAmount.toLocaleString()} DA</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <CreditCard className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">No forfait found</p>
                        <p className="text-xs text-gray-500 mt-1">Rider needs to purchase monthly forfait</p>
                      </div>
                    )}

                    {/* Free Bonus Lessons */}
                    {user.freeBonusLessons > 0 && (
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-3">
                          <Gift className="w-5 h-5 text-purple-600" />
                          <div>
                            <p className="text-sm font-medium text-purple-900">Free Bonus Lessons</p>
                            <p className="text-lg font-bold text-purple-700">{user.freeBonusLessons}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
    </Modal>
  );
}

export default UserDetailsModal;













